import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { Subscription } from 'rxjs';

import { PedidoHistoricoDTO } from '../../../../models/cardapio-digital/cardapio-digital.model';
import { TipoPedido } from '../../../../enums/TipoPedido.enum';
import { formatarPreco as formatarPrecoUtil } from '../../utils/cardapio-preco.util';

@Component({
  selector: 'app-cardapio-historico',
  templateUrl: './cardapio-historico.component.html',
  styleUrls: ['./cardapio-historico.component.scss'],
  providers: [ConfirmationService],
})
export class CardapioHistoricoComponent implements OnInit, OnDestroy {
  @Input() pedidos: PedidoHistoricoDTO[] = [];
  @Input() moedaSimbolo = 'BRL';
  @Input() moedaLocal = 'pt-BR';
  @Output() voltar = new EventEmitter<void>();
  @Output() limparHistorico = new EventEmitter<void>();
  @Output() verTracking = new EventEmitter<PedidoHistoricoDTO>();

  // Textos traduzidos via loadTranslations()
  texts: any = {};

  private subs: Subscription[] = [];

  constructor(
    private translate: TranslateService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.subs.push(
      this.translate.onLangChange.subscribe(() => this.loadTranslations())
    );
    window.scrollTo(0, 0);
  }

  /** Seleciona um pedido: navega para o tracking se ainda estiver em andamento (status ENVIADO),
   *  ou exibe os detalhes locais para pedidos mais antigos sem link WhatsApp */
  selecionarPedido(pedido: PedidoHistoricoDTO): void {
    this.verTracking.emit(pedido);
    window.scrollTo(0, 0);
  }

  /** Volta ao cardapio */
  onVoltar(): void {
    this.voltar.emit();
  }

  /** Abre confirmacao e emite evento de limpeza */
  onLimparHistorico(): void {
    this.confirmationService.confirm({
      header: this.texts.historico?.limpar_titulo || 'Limpar historico',
      message: this.texts.historico?.limpar_confirmacao || 'Deseja remover todos os pedidos do historico?',
      acceptLabel: this.texts.historico?.limpar_sim || 'Sim, limpar',
      rejectLabel: this.texts.historico?.limpar_nao || 'Cancelar',
      icon: 'pi pi-trash',
      accept: () => {
        this.limparHistorico.emit();
      },
    });
  }

  /** Retorna o label traduzido do tipo de pedido */
  getLabelTipoPedido(tipo: TipoPedido): string {
    switch (tipo) {
      case TipoPedido.DELIVERY: return this.texts.historico?.tipo_delivery || 'Delivery';
      case TipoPedido.RETIRADA: return this.texts.historico?.tipo_retirada || 'Retirada';
      case TipoPedido.MESA: return this.texts.historico?.tipo_mesa || 'Mesa';
      default: return '';
    }
  }

  /** Retorna o label de quantidade de itens */
  getLabelItens(quantidade: number): string {
    if (quantidade === 1) {
      return `1 ${this.texts.historico?.itens_count_singular || 'item'}`;
    }
    return `${quantidade} ${this.texts.historico?.itens_count_plural || 'itens'}`;
  }

  /** Retorna a quantidade total de itens do pedido */
  getQuantidadeItens(pedido: PedidoHistoricoDTO): number {
    return pedido.itens.reduce((acc, item) => acc + item.quantidade, 0);
  }

  /** Formata a data ISO para exibicao */
  formatarData(dataIso: string): string {
    try {
      const date = new Date(dataIso);
      const dia = date.getDate().toString().padStart(2, '0');
      const mes = (date.getMonth() + 1).toString().padStart(2, '0');
      const ano = date.getFullYear();
      const hora = date.getHours().toString().padStart(2, '0');
      const minuto = date.getMinutes().toString().padStart(2, '0');
      return `${dia}/${mes}/${ano} ${hora}:${minuto}`;
    } catch {
      return dataIso;
    }
  }

  /** Formata preco usando utilitario do cardapio */
  formatarPreco(valor: number): string {
    return formatarPrecoUtil(valor, this.moedaSimbolo, this.moedaLocal);
  }

  /** Verifica se o pedido e do tipo MESA */
  isMesa(pedido: PedidoHistoricoDTO): boolean {
    return pedido.tipoPedido === TipoPedido.MESA;
  }

  /** Verifica se o pedido e do tipo DELIVERY */
  isDelivery(pedido: PedidoHistoricoDTO): boolean {
    return pedido.tipoPedido === TipoPedido.DELIVERY;
  }

  private loadTranslations(): void {
    this.texts = this.translate.instant('cardapio_digital');
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }
}
